import React, { useMemo, useState } from 'react';
import {
  useReactTable, getCoreRowModel, getSortedRowModel, getFilteredRowModel, getPaginationRowModel,
  flexRender, createColumnHelper, type SortingState,
} from '@tanstack/react-table';
import { ArrowUp, ArrowDown, ArrowUpDown, ChevronLeft, ChevronRight, RotateCcw, Search, Ban } from 'lucide-react';
import { setUserCap, resetUserUsage, setUserDisabled, type TopUserRow } from '../../services/adminService';
import { useConfirm } from '../../hooks/useConfirm';

const usd = (n: number) => `$${(n || 0).toFixed((n || 0) < 1 ? 4 : 2)}`;
const when = (iso: string | null) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' }); } catch { return '—'; }
};
const th = 'py-1.5 pr-3 text-left text-xs uppercase tracking-wide text-secondary-400 select-none';
const td = 'py-2 pr-3 align-middle';

/** Per-user moderation: editable spend cap + reset usage + disable account. */
const CapCell: React.FC<{ uid: string; email: string | null; override?: number; onChange: () => void; setBusy: (b: boolean) => void }> = ({ uid, email, override, onChange, setBusy }) => {
  const [cap, setCap] = useState(override != null ? String(override) : '');
  const [confirm, confirmDialog] = useConfirm();
  const save = async () => { setBusy(true); try { await setUserCap(uid, cap === '' ? -1 : Number(cap)); onChange(); } finally { setBusy(false); } };
  const reset = async () => { setBusy(true); try { await resetUserUsage(uid); onChange(); } finally { setBusy(false); } };
  const disable = async () => {
    if (!(await confirm({ title: 'Disable this account?', description: `Disable ${email || uid.slice(0, 8)}? They will be signed out and blocked until re-enabled.`, confirmText: 'Disable', variant: 'destructive' }))) return;
    setBusy(true); try { await setUserDisabled(uid, true); onChange(); } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-2">
      <input value={cap} onChange={(e) => setCap(e.target.value)} onBlur={save} placeholder="default" type="number" min={0}
        className="w-20 rounded-md border border-secondary-300 dark:border-secondary-700 bg-white dark:bg-secondary-900 px-2 py-1 text-xs" />
      <button onClick={reset} className="text-secondary-400 hover:text-secondary-700 dark:hover:text-secondary-200" title="Reset this user's usage"><RotateCcw className="w-4 h-4" /></button>
      <button onClick={disable} className="text-secondary-400 hover:text-rose-600" title="Disable this user's account"><Ban className="w-4 h-4" /></button>
      {confirmDialog}
    </div>
  );
};

/** Top-users table: email search, sortable columns, and pagination (selectable page size + page number). */
const TopUsersTable: React.FC<{
  users: TopUserRow[]; overrides: Record<string, number>; onChange: () => void; setBusy: (b: boolean) => void; onOpen: (uid: string) => void;
}> = ({ users, overrides, onChange, setBusy, onOpen }) => {
  const [sorting, setSorting] = useState<SortingState>([{ id: 'costUsd', desc: true }]);
  const [filter, setFilter] = useState('');

  const columns = useMemo(() => {
    const col = createColumnHelper<TopUserRow>();
    return [
      col.accessor('email', {
        header: 'User',
        cell: (c) => <button onClick={() => onOpen(c.row.original.uid)} className="text-left text-secondary-900 dark:text-white hover:underline" title="View usage breakdown">{c.getValue() || c.row.original.uid.slice(0, 8)}</button>,
      }),
      col.accessor('requests', { header: 'Requests', cell: (c) => c.getValue().toLocaleString() }),
      col.accessor('tokens', { header: 'Tokens', cell: (c) => c.getValue().toLocaleString() }),
      col.accessor('costUsd', { header: 'Spend', cell: (c) => usd(c.getValue()) }),
      col.accessor('lastActive', { header: 'Last active', cell: (c) => <span className="whitespace-nowrap text-secondary-500">{when(c.getValue())}</span> }),
      col.display({ id: 'cap', header: 'Moderation', cell: (c) => <CapCell uid={c.row.original.uid} email={c.row.original.email} override={overrides[c.row.original.uid]} onChange={onChange} setBusy={setBusy} /> }),
    ];
  }, [overrides, onChange, setBusy, onOpen]);

  const table = useReactTable({
    data: users,
    columns,
    state: { sorting, globalFilter: filter },
    onSortingChange: setSorting,
    onGlobalFilterChange: setFilter,
    globalFilterFn: (row, _id, value) => {
      const q = String(value).toLowerCase();
      return (row.original.email || '').toLowerCase().includes(q) || row.original.uid.toLowerCase().includes(q);
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: 10 } },
  });

  const pageCount = table.getPageCount();
  const pageIndex = table.getState().pagination.pageIndex;

  return (
    <div>
      <div className="mb-2 flex items-center gap-2">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-400" />
          <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Search email…"
            className="w-full rounded-md border border-secondary-300 dark:border-secondary-700 bg-white dark:bg-secondary-900 pl-8 pr-2 py-1.5 text-sm focus:outline-none focus:border-secondary-500" />
        </div>
        <span className="text-xs text-secondary-400">{table.getFilteredRowModel().rows.length} users</span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((h) => {
                  const sortable = h.column.getCanSort();
                  const dir = h.column.getIsSorted();
                  return (
                    <th key={h.id} className={th}>
                      {sortable ? (
                        <button onClick={h.column.getToggleSortingHandler()} className="inline-flex items-center gap-1 hover:text-secondary-700 dark:hover:text-secondary-200">
                          {flexRender(h.column.columnDef.header, h.getContext())}
                          {dir === 'asc' ? <ArrowUp className="w-3 h-3" /> : dir === 'desc' ? <ArrowDown className="w-3 h-3" /> : <ArrowUpDown className="w-3 h-3 opacity-40" />}
                        </button>
                      ) : flexRender(h.column.columnDef.header, h.getContext())}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id} className="border-t border-secondary-100 dark:border-secondary-800">
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className={td}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-secondary-500">
        <label className="flex items-center gap-1.5">Rows per page
          <select value={table.getState().pagination.pageSize} onChange={(e) => table.setPageSize(Number(e.target.value))}
            className="rounded-md border border-secondary-300 dark:border-secondary-700 bg-white dark:bg-secondary-900 px-1.5 py-1">
            {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
        <div className="flex items-center gap-2">
          <button onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()} className="p-1 rounded-md hover:bg-secondary-100 dark:hover:bg-secondary-800 disabled:opacity-30" aria-label="Previous page"><ChevronLeft className="w-4 h-4" /></button>
          <span>Page
            <input type="number" min={1} max={Math.max(1, pageCount)} value={pageIndex + 1}
              onChange={(e) => { const p = Math.min(Math.max(1, Number(e.target.value) || 1), Math.max(1, pageCount)); table.setPageIndex(p - 1); }}
              className="w-12 mx-1 rounded-md border border-secondary-300 dark:border-secondary-700 bg-white dark:bg-secondary-900 px-1 py-0.5 text-center" />
            of {Math.max(1, pageCount)}</span>
          <button onClick={() => table.nextPage()} disabled={!table.getCanNextPage()} className="p-1 rounded-md hover:bg-secondary-100 dark:hover:bg-secondary-800 disabled:opacity-30" aria-label="Next page"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>
    </div>
  );
};

export default TopUsersTable;
